import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
} from 'lexical';
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableNode,
  INSERT_TABLE_COMMAND,
} from '@lexical/table';
import { $insertNodeToNearestRoot } from '@lexical/utils';
import { Button } from 'primereact/button';
import { InputNumber } from 'primereact/inputnumber';
import { richTextEditorStrings } from '../../../i18n/components/richtTextEditor';

interface TableDimensions {
  rows: number;
  columns: number;
}

export function TablePlugin() {
  const [editor] = useLexicalComposerContext();
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [isInTable, setIsInTable] = useState(false);
  const [dimensions, setDimensions] = useState<TableDimensions>({
    rows: 3,
    columns: 3,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const updateTableState = useCallback(() => {
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) {
      setIsInTable(false);
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const isRootNode = anchorNode.getKey() === 'root';
    const element = isRootNode
      ? anchorNode
      : anchorNode.getTopLevelElementOrThrow();

    let tableNode = null;
    if ($isTableNode(element)) {
      tableNode = element;
    } else if (element.getParent() && $isTableNode(element.getParent())) {
      tableNode = element.getParent();
    }

    setIsInTable(tableNode !== null);
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateTableState();
      });
    });
  }, [editor, updateTableState]);

  useEffect(() => {
    return editor.registerCommand(
      INSERT_TABLE_COMMAND,
      ({ columns, rows }: { columns: string; rows: string }) => {
        const numRows = parseInt(rows, 10);
        const numColumns = parseInt(columns, 10);

        if (numRows <= 0 || numColumns <= 0) {
          return true;
        }

        const tableNode = $createTableNode();

        for (let i = 0; i < numRows; i++) {
          const tableRowNode = $createTableRowNode();

          for (let j = 0; j < numColumns; j++) {
            const cellNode = $createTableCellNode(0);
            const paragraphNode = $createParagraphNode();
            cellNode.append(paragraphNode);
            tableRowNode.append(cellNode);
          }

          tableNode.append(tableRowNode);
        }

        $insertNodeToNearestRoot(tableNode);
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  const handleInsertTable = useCallback(() => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, {
      columns: String(dimensions.columns),
      rows: String(dimensions.rows),
    });
    setShowTableDialog(false);
    setDimensions({ rows: 3, columns: 3 });
  }, [editor, dimensions]);

  const handleOpenDialog = useCallback(() => {
    setShowTableDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setShowTableDialog(false);
    setDimensions({ rows: 3, columns: 3 });
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleInsertTable();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        handleCloseDialog();
      }
    },
    [handleInsertTable, handleCloseDialog],
  );

  return (
    <span className="lexical-toolbar-group" style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpenDialog}
        className={`lexical-toolbar-button ${isInTable ? 'active' : ''}`}
        aria-label={richTextEditorStrings.table.button.label}
        title={richTextEditorStrings.table.button.title}
      >
        <i className="pi pi-table" />
      </button>

      {showTableDialog && (
        <div
          className="lexical-table-dialog-popover"
          role="dialog"
          aria-label={richTextEditorStrings.table.dialog.label}
        >
          <div className="lexical-table-dialog-content">
            <h3 className="lexical-table-dialog-title">
              {richTextEditorStrings.table.dialog.title}
            </h3>

            <div className="lexical-table-dialog-field">
              <label
                htmlFor="table-rows-input"
                className="lexical-table-dialog-label"
              >
                {richTextEditorStrings.table.dialog.rows.label}
              </label>
              <InputNumber
                inputId="table-rows-input"
                min={1}
                max={20}
                value={dimensions.rows}
                onValueChange={(e) =>
                  setDimensions((prev) => ({
                    ...prev,
                    rows: e.value ?? 1,
                  }))
                }
                onKeyDown={handleKeyDown}
                aria-required="true"
                showButtons
                style={{ width: '100%' }}
              />
            </div>

            <div className="lexical-table-dialog-field">
              <label
                htmlFor="table-columns-input"
                className="lexical-table-dialog-label"
              >
                {richTextEditorStrings.table.dialog.columns.label}
              </label>
              <InputNumber
                inputId="table-columns-input"
                min={1}
                max={20}
                value={dimensions.columns}
                onValueChange={(e) =>
                  setDimensions((prev) => ({
                    ...prev,
                    columns: e.value ?? 1,
                  }))
                }
                onKeyDown={handleKeyDown}
                aria-required="true"
                showButtons
                style={{ width: '100%' }}
              />
            </div>

            <div className="lexical-table-dialog-actions">
              <Button
                type="button"
                onClick={handleInsertTable}
                aria-label={richTextEditorStrings.table.actions.insert.label}
                icon="pi pi-check"
                label={richTextEditorStrings.table.actions.insert.text}
                size="small"
              />
              <Button
                type="button"
                onClick={handleCloseDialog}
                aria-label={richTextEditorStrings.table.actions.cancel.label}
                icon="pi pi-times"
                label={richTextEditorStrings.table.actions.cancel.text}
                outlined
                size="small"
              />
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
